<template>
    <span>
        <v-btn small outlined :color="total ? 'warning' : 'grey'" :loading="cargando" @click="abrir"
               title="Números de registro repetidos o provisionales que necesitan revisión">
            <v-icon small left>report_problem</v-icon>
            Registros por revisar
            <v-chip x-small label class="ml-2" :color="total ? 'warning' : 'grey lighten-1'" text-color="white" v-if="total !== null">
                {{ total }}
            </v-chip>
        </v-btn>

        <v-dialog v-model="visible" max-width="1100" scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="warning">report_problem</v-icon>
                    Números de registro por revisar
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="visible = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>

                <v-card-text>
                    <v-alert dense text type="info" class="mb-3">
                        Desde ahora el sistema no permite números de registro repetidos, ni al importar ni al editar.
                        Estos casos vienen de cargas anteriores. No bloquean nada, pero conviene corregirlos:
                        {{ editable ? 'abra el curso con el lápiz y asigne el número correcto.' : 'corríjalos en el Excel de origen y vuelva a importar.' }}
                    </v-alert>

                    <v-tabs v-model="pestana" grow>
                        <v-tab>Repetidos ({{ duplicados.length }})</v-tab>
                        <v-tab>No numéricos ({{ noNumericos.length }})</v-tab>
                    </v-tabs>

                    <p class="text-caption grey--text text--darken-1 mt-3 mb-2">
                        {{ pestana === 0
                            ? 'El mismo número está asignado a más de un curso activo.'
                            : 'Números provisionales o con formato inválido: no son un registro real del INFOP.' }}
                    </p>

                    <v-data-table
                        dense class="elevation-1"
                        :headers="columnas"
                        :items="pestana === 0 ? duplicados : noNumericos"
                        item-key="curso_id"
                        :items-per-page="15"
                        :footer-props="{'items-per-page-options': [15, 50, 100], 'items-per-page-text': 'Filas'}"
                    >
                        <template v-slot:item="{ item }">
                            <tr :class="{'grupo-alterno': pestana === 0 && grupo(item.registro) % 2 === 1}">
                                <td class="font-weight-bold text-no-wrap">{{ item.registro }}</td>
                                <td class="text-no-wrap">{{ item.numero_identidad }}</td>
                                <td>{{ item.nombres_apellidos }}</td>
                                <td>{{ item.curso }}</td>
                                <td class="text-center">{{ item.anyo }}</td>
                                <td class="text-no-wrap">{{ item.importacion_numero || '—' }}</td>
                                <td class="text-center" v-if="editable">
                                    <v-btn x-small outlined color="info" @click="editar(item)" title="Corregir el número de registro">
                                        <v-icon x-small>edit</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                        <template v-slot:no-data>
                            <v-alert dense type="success" outlined class="text-center mt-3">
                                Sin casos pendientes.
                            </v-alert>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </span>
</template>

<script>
/**
 * Botón con contador y diálogo de los números de registro que requieren
 * revisión: duplicados históricos y números no numéricos.
 *
 *   <registros-conflicto endpoint="/importaciones/registros_conflicto" editable/>
 */
export default {
    name: "RegistrosConflicto",
    props: {
        endpoint: {type: String, required: true},
        // Los cursos de certificados se corrigen en pantalla; las constancias no tienen edición.
        editable: {type: Boolean, default: false},
    },
    data() {
        return {
            visible: false,
            cargando: false,
            pestana: 0,
            total: null,
            duplicados: [],
            noNumericos: [],
        }
    },
    computed: {
        columnas() {
            const columnas = [
                {text: 'N.° de registro', value: 'registro'},
                {text: 'Identidad', value: 'numero_identidad'},
                {text: 'Participante', value: 'nombres_apellidos'},
                {text: 'Curso', value: 'curso'},
                {text: 'Año', value: 'anyo', align: 'center'},
                {text: 'Importación', value: 'importacion_numero'},
            ];

            if (this.editable) columnas.push({text: 'Corregir', align: 'center', sortable: false});

            return columnas;
        },
        // Posición de cada número repetido, para alternar el fondo por grupo.
        grupos() {
            const grupos = {};
            this.duplicados.forEach(d => {
                if (!(d.registro in grupos)) grupos[d.registro] = Object.keys(grupos).length;
            });
            return grupos;
        },
    },
    methods: {
        grupo(registro) {
            return this.grupos[registro] || 0;
        },
        cargar() {
            this.cargando = true;

            return window.axios.get(this.endpoint).then(response => {
                if (response.data.success) {
                    this.duplicados = response.data.data.duplicados;
                    this.noNumericos = response.data.data.no_numericos;
                    this.total = this.duplicados.length + this.noNumericos.length;
                }
            })
                .catch(() => {
                    this.total = null;
                })
                .finally(() => {
                    this.cargando = false;
                });
        },
        abrir() {
            this.visible = true;
            this.cargar();
        },
        editar(item) {
            const token = window.btoa(item.curso_id);
            const ruta = this.$router.resolve({name: 'CursosEditar', query: {token}});
            window.open(ruta.href, '_blank');
        },
    },
    created() {
        this.cargar();
    },
}
</script>

<style scoped>
.grupo-alterno {
    background: #f6f7fb;
}
</style>
